import React from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../common/SelectInput";
import TextInput from "../common/TextInput";
import tailwindClassnames from "./TailwindClassnames";

type UsersSearchBarProps = {
  userSearchQuery: any;
  onInviteUser: Function;
  loading: boolean;
  sendingInvite: boolean;
  roles: any[];
  onChangeSearch: Function;
  onSearch: Function;
  onChangeSelectRole: Function;
  handleRoleSelectEnter: Function;
  hasInvitePermission: boolean;
};

/**
 * Filter toolbar for the org/project Users tabs. Renders as a standalone
 * toolbar card (the user tables below it render their own tableCard
 * surfaces, so this toolbar carries its own card rather than attaching to
 * one of theirs).
 */
const UsersSearchBar: React.FC<UsersSearchBarProps> = ({
  userSearchQuery,
  onInviteUser,
  loading,
  roles,
  onChangeSearch,
  onSearch,
  onChangeSelectRole,
  handleRoleSelectEnter,
  hasInvitePermission,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`${tailwindClassnames.tableCard} mb-4`}>
      {/* border-b-0: the toolbar is this card's only child, so the usual
          bottom hairline would dangle. */}
      <div className={`${tailwindClassnames.tableCardToolbar} border-b-0`}>
        <TextInput
          name={"first_name"}
          label={t("First Name")}
          placeholder={"Search first name"}
          value={userSearchQuery.first_name || ""}
          error={""}
          disabled={false}
          className={tailwindClassnames.tableFilterInput}
          type={"text"}
          initialFocus={true}
          onChange={(e: any) => onChangeSearch(e)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
        <TextInput
          name={"last_name"}
          label={t("Last Name")}
          placeholder={"Search last name"}
          value={userSearchQuery.last_name || ""}
          error={""}
          disabled={false}
          className={tailwindClassnames.tableFilterInput}
          type={"text"}
          initialFocus={false}
          onChange={(e: any) => onChangeSearch(e)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
        <TextInput
          name={"email"}
          label={t("Email")}
          placeholder={"Search email"}
          value={userSearchQuery.email || ""}
          error={""}
          disabled={false}
          className={tailwindClassnames.tableFilterInput}
          type={"text"}
          initialFocus={false}
          onChange={(e: any) => onChangeSearch(e)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
        <SelectInput
          options={roles.map((role) => ({
            value: role.value,
            label: t(role.name),
          }))}
          className={"w-48 text-left text-sm"}
          isDisabled={false}
          isLoading={loading}
          isClearable={true}
          isSearchable={true}
          isMulti={false}
          name={t("Role")}
          onChange={(e: any) => onChangeSelectRole(e ? e.value : null)}
          errors={""}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              handleRoleSelectEnter(e);
            }
          }}
          searchBar={true}
          usePortal={true}
        />
        <button
          className={tailwindClassnames.btnFilterApply}
          onClick={() => onSearch()}
        >
          {t("Apply filters")}
        </button>
        <div className="flex-1" />
        {/* Conditional rendering, not a "hidden" class swap, so the gate
            can't be defeated by display utilities. */}
        {hasInvitePermission && (
          <button
            onClick={() => onInviteUser()}
            className={tailwindClassnames.btnPrimary}
          >
            <span aria-hidden="true">+ </span>
            {t("Invite User")}
          </button>
        )}
      </div>
    </div>
  );
};

export default UsersSearchBar;
